import React, { ReactNode } from 'react';
import { HelpEmailLink, HelpPhoneLink } from '../components/common/HelpLinks';
import { androidDownloadLink, iOSDownloadLink } from '../utilities/Utils';

const termsPdf = require('../pdfs/Tour24_Terms_of_ Use_09.2022.pdf');

export const tour24Faqs: {
    question: string;
    answer: ReactNode;
    fullFaqsOnly: boolean;
    webTourFaq?: boolean;
}[] = [
    {
        question: 'WHAT IS TOUR24?',
        answer: 'Tour24 is a self-guided tour app where you can schedule a time to tour a community on \
            your own, without a leasing agent present. Tour24 uses your phone as a key to provide \
            temporary access, and to guide you around the community. It works on iOS and Android mobile phones.',
        fullFaqsOnly: false
    },
    {
        question: 'HOW DO I GET STARTED? HOW DO I REGISTER?',
        answer: (
            <>
                <ul>
                    <li>
                        Click the Self-Guided Tour registration link on a community’s website or
                        scan a QR code at a community.
                    </li>
                    <li>
                        Fill out the registration information, select a date/time to tour, and
                        provide ID verification. This process takes about 3 minutes.
                    </li>
                    <li>
                        You will receive a confirmation email or text that will include a link to
                        download the app.
                    </li>
                    <li>
                        Prior to your tour, Tour24 will send directions for accessing the community.
                    </li>
                </ul>
                NOTE: Sign up using your name EXACTLY as it appears on your ID. Do not use a
                nickname such as Liz instead of Elizabeth. Our verification process will confirm
                that your ID, Selfie and credit card all match.
            </>
        ),
        fullFaqsOnly: false
    },
    {
        question: 'HOW DO I USE THE APP?',
        answer: (
            <>
                Download the Tour24 Self-Guided Tour App from the{' '}
                <a href={iOSDownloadLink}>App Store</a> or{' '}
                <a href={androidDownloadLink}>Google Play</a> and login using the same email and
                password used for your initial registration. Make sure that you have Bluetooth
                enabled on your phone when you arrive at the community for the app to work. For
                other questions, call Tour24 Support: <HelpPhoneLink /> or email: <HelpEmailLink />
            </>
        ),
        fullFaqsOnly: false
    },
    {
        question: 'WHAT DO I NEED TO DO BEFORE MY TOUR STARTS?',
        answer: (
            <>
                <ul>
                    <li>
                        Download the Tour24 app (<a href={iOSDownloadLink}>App Store</a> /{' '}
                        <a href={androidDownloadLink}>Google Play</a>).
                    </li>
                    <li>Turn on Bluetooth on your phone.</li>
                    <li>
                        Review the last email/text sent from Tour24 for any special instructions
                        regarding your tour (i.e. parking location).
                    </li>
                    <li>
                        Confirm that you have arrived at the community during your tour time. (Note:
                        Access is limited to your registered tour time only.)
                    </li>
                    <li>
                        Login to the app using the same email and password used for your initial
                        registration.
                    </li>
                    <li>
                        Find your tour on the{' '}
                        <i>
                            <b>My Tours</b>
                        </i>{' '}
                        page and select{' '}
                        <i>
                            <b>Tour Now</b>
                        </i>
                        .
                    </li>
                </ul>
            </>
        ),
        fullFaqsOnly: false
    },
    {
        question: 'WHY DO YOU NEED MY CREDIT CARD?',
        answer: (
            <>
                There is no charge for the tour. Your credit card may be used as an additional layer
                of ID verification. Please also refer to the{' '}
                <a href={termsPdf} style={{ color: 'red' }}>
                    Tour24 Terms of Use
                </a>{' '}
                regarding credit card authorization should any damages occur during your tour.
            </>
        ),
        fullFaqsOnly: false
    },
    {
        question: 'HOW DO I TURN ON BLUETOOTH?',
        answer: (
            <>
                Make sure that Bluetooth is enabled on your mobile phone to use Tour24. Typically,
                Bluetooth is found under{' '}
                <i>
                    <b>Settings</b>
                </i>{' '}
                on your phone.
            </>
        ),
        fullFaqsOnly: true
    },
    {
        question: 'WHERE DO I PARK?',
        answer: 'Parking instructions will be displayed when you start your tour through the Tour24 \
        app OR they will be sent 2 hours in advance of your tour by email or text. If your tour \
        appointment is within 2 hours from registration, instructions may be sent immediately.',
        fullFaqsOnly: true
    },
    {
        question: 'WHERE DO I START MY TOUR?',
        answer: (
            <>
                Instructions will be provided in the Tour24 app. Login to the Tour24 app using your
                email address and password and locate your tour under the{' '}
                <i>
                    <b>My Tours</b>
                </i>{' '}
                Page. Select{' '}
                <i>
                    <b>Start Tour</b>
                </i>
                .
            </>
        ),
        fullFaqsOnly: true
    },
    {
        question: 'HOW DO I GET IN TO THE COMMUNITY?',
        answer: 'Access instructions will be provided within the Tour24 app and/or sent by email \
        or text just prior to your tour. During the tour, you will be prompted with instructions \
        on how to open each lock. (Enter a code, tap “Unlock” in the app, etc.)',
        fullFaqsOnly: true
    },
    {
        question: 'HOW LONG IS MY TOUR ACTIVE?',
        answer: (
            <>
                Tours are only valid from the start to the end time of your appointment. If your
                tour time has expired, please visit the{' '}
                <i>
                    <b>My Tours</b>
                </i>{' '}
                page in the Tour24 app, find your missed tour, and select{' '}
                <i>
                    <b>Reschedule Tour</b>
                </i>
                .
            </>
        ),
        fullFaqsOnly: false
    },
    {
        question: 'HOW DO I LEASE AN APARTMENT AFTER I TOUR?',
        answer: (
            <>
                You may link to an online application at the end of the tour or find your completed
                tour in the{' '}
                <i>
                    <b>My Tours</b>
                </i>{' '}
                Page and select{' '}
                <i>
                    <b>“Apply Now to Lease.”</b>
                </i>{' '}
                You can also contact the community directly.
            </>
        ),
        fullFaqsOnly: true
    },
    {
        question: 'HOW DO I OPEN THE APARTMENT DOOR?',
        answer: 'Instructions are provided within the Tour24 app. Please make sure that you have \
        downloaded the app and you are logged into your scheduled tour. During the tour, you will \
        be prompted with instructions on how to open each lock. (Enter a code, tap “Unlock” in \
        the app, etc.)',
        fullFaqsOnly: true
    },
    {
        question: 'WHO DO I CONTACT WITH A LOCK OR ACCESS QUESTION?',
        answer: (
            <>
                If you have a specific question about access while at the community, call Tour24
                Support: <HelpPhoneLink /> or email: <HelpEmailLink />.
            </>
        ),
        fullFaqsOnly: true
    },
    {
        question: 'WHAT IS TOUR24?',
        answer: (
            <p>
                Tour24 is a self-guided tour app where you can schedule a time to tour a community
                on your own, without a leasing agent present. Tour24 uses your phone as a key to
                provide temporary access, and to guide you around the community through an
                interactive tour including audio and visuals. It works on iOS and Android mobile
                phones
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'HOW DO I GET STARTED? HOW DO I REGISTER?',
        answer: (
            <p>
                <ul>
                    <li>
                        Click the Self-Guided Tour registration link on a community’s website or
                        scan a QR code at a community.
                    </li>
                    <li>
                        Fill out the registration information, select a date/time to tour, and
                        provide ID verification. This process takes about 3 minutes.
                    </li>
                    <li>
                        You will receive a confirmation email and text that will include Community
                        access and Self-Guided Tour instructions.
                    </li>
                </ul>
                NOTE: Sign up using your name EXACTLY as it appears on your ID. Do not use a
                nickname such as Liz instead of Elizabeth. Our verification process will confirm
                that your ID, Selfie and credit card all match.
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'WHAT DO I NEED TO DO BEFORE MY TOUR STARTS?',
        answer: (
            <p>
                <ul>
                    <li>
                        Review the last email/text sent from Tour24 for any special instructions
                        regarding your tour (i.e. parking location).
                    </li>
                    <li>
                        Confirm that you have arrived at the community during your tour time. (Note:
                        Access is only available for your registered tour time.)
                    </li>
                    <li>
                        You will receive your temporary access codes 10 minutes before your
                        appointment time via email and text message.
                    </li>
                    <li>
                        Log into your Tour24 account using the phone number and password used for
                        your initial registration.
                    </li>
                    <li>
                        Find your tour on the{' '}
                        <i>
                            <b>My Tours</b>
                        </i>{' '}
                        page and select{' '}
                        <i>
                            <b>Start Tour</b>
                        </i>
                        .
                    </li>
                </ul>
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'WHY DO YOU NEED MY CREDIT CARD?',
        answer: (
            <p>
                There is no charge for the tour. Your credit card may be used as an additional layer
                of ID verification. Please also refer to the{' '}
                <a href={termsPdf} style={{ color: 'red' }}>
                    Tour24 Terms of Use
                </a>{' '}
                regarding credit card authorization should any damages occur during your tour.
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'WHERE DO I PARK?',
        answer: (
            <p>
                Parking instructions are included in your access instructions email and text
                messages.
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'WHERE DO I START MY TOUR?',
        answer: (
            <p>
                Tour instructions will be included in your Access email and text message. Tour
                details will also display in your Tour24 Account. Sign in to Tour24, go to{' '}
                <i>
                    <b>My Tours</b>
                </i>{' '}
                and select{' '}
                <i>
                    <b>Start Tour.</b>
                </i>
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'HOW DO I GET IN TO THE COMMUNITY?',
        answer: (
            <p>
                Access instructions will be provided via email and text message. Access codes will
                also display in your Tour24 Account within 10 minutes of your appointment time. Sign
                in to Tour24, go to{' '}
                <i>
                    <b>My Tours</b>
                </i>{' '}
                and select{' '}
                <i>
                    <b>Start Tour.</b>
                </i>
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'HOW LONG IS MY TOUR ACTIVE?',
        answer: (
            <p>
                Tours are only valid during your appointment. If your tour time has expired, sign in
                to Tour24, go to{' '}
                <i>
                    <b>My Tours</b>
                </i>
                , find your missed tour, and select{' '}
                <i>
                    <b>Reschedule Tour</b>
                </i>
                .
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'HOW DO I LEASE AN APARTMENT AFTER I TOUR?',
        answer: (
            <p>
                There are a few ways to lease an apartment. You can tap the <b>"Apply"</b> icon on
                the Tour Home Page. There is an <b>"Apply Now to Lease"</b> link on the{' '}
                <b>My Tours</b> page. You can also contact the community directly via the Ask page.
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'HOW DO I OPEN THE APARTMENT DOOR?',
        answer: (
            <p>
                Access codes and instructions are provided via email and text message. They will
                also display in your Tour24 Account. Sign in to Tour24, go to{' '}
                <i>
                    <b>My Tours</b>
                </i>{' '}
                and select{' '}
                <i>
                    <b>Start Tour.</b>
                </i>
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    },
    {
        question: 'WHO DO I CONTACT WITH A LOCK OR ACCESS QUESTION?',
        answer: (
            <p>
                If you have a specific question about access while at the community, call Tour24
                Support: <HelpPhoneLink /> or email: <HelpEmailLink />.
            </p>
        ),
        fullFaqsOnly: false,
        webTourFaq: true
    }
];



// WEBPACK FOOTER //
// ./src/data/information.tsx